import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import useSEO from "../hooks/useSEO";
import ParticleNetwork from "../components/ParticleNetwork";
import SectionHeading from "../components/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { BRAND, FOUNDER_BIO, FOUNDER_TAGS, VALUES } from "../data/site";

const FOUNDER = "/assets/pankaj-sharma.png";
const LOGO = "/assets/ats-logo.png";

export default function About() {
  useSEO({
    title: "About ATS IT Solutions — Pankaj Sharma, Founder & CEO",
    description:
      "ATS IT Solutions (ATS) stands for Atmasamman Technology Services. Meet Pankaj Sharma, Founder & CEO — 16+ years of enterprise technology, AI, cloud and digital payments experience across IBM, Mastercard, EY and more.",
  });

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden bg-navy-deep">
        <div className="absolute inset-0 z-0 opacity-60">
          <ParticleNetwork className="w-full h-full" density={0.6} />
        </div>
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-brand-orange/10 blur-[160px] z-0" />
        <div className="container-x relative z-10 text-center flex flex-col items-center">
          <Reveal>
            <span className="overline">About ATS IT Solutions</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mt-4 max-w-3xl">
              The story behind <span className="text-gradient-orange">ATS</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p data-testid="about-brand-statement" className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              ATS IT Solutions (ATS) is the customer-facing technology solutions brand. ATS stands for {BRAND.expanded}. ATS IT Solutions operates as a brand of {BRAND.legal}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-4 text-base text-slate-400 max-w-2xl leading-relaxed">
              Built on 16+ years of enterprise technology experience — engineered for trust, scale, and real business value.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founder Profile */}
      <section data-testid="founder-section" className="relative py-20 md:py-28 bg-navy">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5 lg:sticky lg:top-28"
            >
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-brand-orange/40 via-brand-orange/10 to-transparent blur-xl" />
                <div className="relative rounded-[2rem] overflow-hidden border border-white/15 shadow-2xl shadow-black/50">
                  <img
                    src={FOUNDER}
                    alt="Pankaj Sharma, Founder and CEO of ATS IT Solutions."
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </motion.div>

            <div className="lg:col-span-7">
              <Reveal>
                <h2 className="font-head text-3xl md:text-4xl font-bold text-white">Pankaj Sharma</h2>
                <p className="text-brand-orange font-medium mt-2 text-lg">Founder &amp; CEO – ATS IT Solutions</p>
              </Reveal>

              <Reveal delay={0.1}>
                <div className="flex flex-wrap gap-2 mt-6">
                  {FOUNDER_TAGS.map((tag) => (
                    <span
                      key={tag}
                      data-testid={`founder-tag-${tag.split(" ")[0].toLowerCase()}`}
                      className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-slate-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </Reveal>

              <div className="mt-8 space-y-5">
                {FOUNDER_BIO.map((p, i) => (
                  <Reveal key={i} delay={0.05 * i}>
                    <p className="text-slate-300 leading-[1.8] text-base md:text-lg">{p}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand strip */}
      <section className="relative py-16 bg-navy-deep border-y border-white/10">
        <div className="container-x flex flex-col items-center text-center gap-5">
          <Reveal>
            <img src={LOGO} alt={`${BRAND.name} logo`} className="h-36 w-36 md:h-44 md:w-44 rounded-full object-contain shadow-2xl shadow-black/40" />
          </Reveal>
          <Reveal delay={0.05}>
            <p className="font-head text-slate-400 tracking-wide">
              {BRAND.name} — <span className="text-white">Engineering Reliable Technology</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p data-testid="about-brand-legal" className="text-slate-500 text-sm">
              {BRAND.name} is a brand of {BRAND.legal} · {BRAND.domain}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section data-testid="values-section" className="relative py-24 md:py-32 bg-navy">
        <div className="container-x">
          <SectionHeading
            align="center"
            overline="Our Philosophy"
            title="The values behind everything we build"
            subtitle="Principles drawn straight from how ATS works with every client."
          />
          <StaggerGroup className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <StaggerItem key={v.title}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="glass rounded-3xl p-8 h-full text-center flex flex-col items-center card-glow"
                  >
                    <span className="w-14 h-14 rounded-2xl bg-brand-orange/15 grid place-items-center mb-5">
                      <Icon size={26} className="text-brand-orange" />
                    </span>
                    <h3 className="font-head text-xl font-semibold text-white">{v.title}</h3>
                    <p className="text-slate-400 text-sm mt-3 leading-relaxed">{v.desc}</p>
                  </motion.div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative py-24 md:py-28 bg-navy">
        <div className="container-x text-center flex flex-col items-center">
          <Reveal>
            <h2 className="font-head text-3xl md:text-5xl font-semibold text-white max-w-3xl">
              Let's build something <span className="text-gradient-orange">reliable</span> together
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact" data-testid="about-contact-cta" className="btn-primary mt-8 text-base">
              Get in Touch <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
